<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import config from '@/config/ConfigProvider';
import { useLocale, useLocalisation } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import { useRoute } from 'vue-router';

type Props = {
  searchLabel?: string;
};

defineProps<Props>();

const { t, l } = useLocale();
const { language } = useLocalisation();
const serviceName = ref<string>(config.value('serviceName'));
const logoImageFile = ref<string>(config.value('logoImageFile'));
const showGuides = ref<string>(config.value('showGuides'));
const showMaterials = ref<string>(config.value('showMaterials'));
const countryCode = ref<string>(config.value('countryCode'));
const { toggleBurgerMenu, closeBurgerMenu } = useGuiStore();

const capitalizationClass = computed(() => (language.value === 'en-GB' ? 'text-capitalize' : 'text-capitalize-first'));
const logoImageUrl = computed(() => new URL(`/src/assets/images/${logoImageFile.value}`, import.meta.url).href);

const tabs = [
  { path: '/videos', icon: 'custom:videoIcon', label: l.tabs.films, disabled: false },
  { path: '/guides', icon: 'custom:guideIcon', label: l.tabs.guides, disabled: !showGuides.value },
  { path: '/materials', icon: 'mdi-file-document-outline', label: l.tabs.materials, disabled: !showMaterials.value },
];
const activeTab = ref<number>(0);
const route = useRoute();
watch(
  route,
  (newRoute) => {
    const tab = tabs.findIndex((t) => newRoute.path.startsWith(t.path));
    if (tab !== -1) {
      activeTab.value = tab;
    }
  },
  { immediate: true },
);
</script>

<template>
  <v-app-bar color="surface-container-lowest">
    <v-row no-gutters style="max-width: none">
      <v-col cols="12" md="3" lg="2" class="d-flex align-center">
        <div class="d-flex align-center justify-start">
          <v-app-bar-nav-icon
            class="nav-button"
            :aria-label="t(l.label_burger_menu_button)"
            @click="toggleBurgerMenu"
            @keydown.esc="closeBurgerMenu"
          />
          <v-app-bar-title class="toolbar-title pl-1 pl-sm-2">
            <router-link :to="{ name: 'begreppaLanding' }">
              <v-img
                :src="logoImageUrl"
                height="100%"
                max-height="30"
                width="140"
                cover
                :alt="`${serviceName} ${t(l.home)}`"
              />
            </router-link>
          </v-app-bar-title>
        </div>
      </v-col>
      <v-col cols="7" lg="8">
        <nav v-if="countryCode === 'se'" class="d-none d-md-block text-brand">
          <v-list
            class="justify-center d-flex flex-row bg-transparent py-0 toolbar-links"
            variant="plain"
            tag="ul"
            :aria-label="t(l.label_main_navigation)"
            role="list"
          >
            <li v-for="tab in tabs" :key="tab.path" v-show="!tab.disabled">
              <v-list-item :to="tab.path" class="h-100" active-class="active-nav-link">
                <template #prepend>
                  <v-icon class="me-2" size="18"> {{ tab.icon }} </v-icon>
                </template>
                <v-list-item-title :class="capitalizationClass" class="tab-text"> {{ t(tab.label) }}</v-list-item-title>
              </v-list-item>
            </li>
          </v-list>
        </nav>
      </v-col>
    </v-row>
  </v-app-bar>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';

.v-app-bar {
  :deep(.v-toolbar__content) {
    padding: 4px 16px;
  }
}
:deep(.toolbar-links) {
  .v-list-item__spacer {
    display: none;
  }
  .v-list-item {
    opacity: 1;
    .v-icon,
    .v-list-item__title {
      opacity: 1;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: var(--v-theme-primary);
    }
  }
  .active-nav-link {
    $active-link-border-width: 2px;
    border-bottom-width: $active-link-border-width;
    color: var(--v-theme-primary);
    border-bottom-color: var(--v-theme-primary);
    .v-list-item__prepend,
    .v-list-item__content {
      margin-bottom: calc($active-link-border-width * -1); // trick to not make the text bounce
    }
  }
}

.tab-text {
  font-size: 14px; /* v-tab uses this */
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.0892857143em;
}

.nav-button {
  color: rgba(var(--v-theme-primary)) !important;
  transition:
    0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
    visibility 0s;

  .v-icon {
    transition: none;
  }

  &:hover {
    color: white !important;
    background: rgb(var(--v-theme-brand));
  }

  &:focus {
    outline: none !important;
  }
}

.text-capitalize-first {
  text-transform: lowercase;
}

.text-capitalize-first::first-letter {
  text-transform: uppercase;
}
</style>
